/**
 * RenderImageStep.tsx
 *
 * Interactive render image builder — replaces the old PreviewStep + GenerateStep
 * for the render wizard. Inspired by ImageGenerationStep from the presentation
 * builder but simplified for renders (no slides, no slide mock previews).
 *
 * Layout (NO page scroll — internal panels scroll):
 *
 *   ┌─ .container (flex column, height: 100%) ───────────────────────────────┐
 *   │  .toolbar (flex-shrink: 0)                                              │
 *   │  .editorArea (flex: 1, min-height: 0)                                   │
 *   │    .sidebar (200px, scrollable render thumbnails)                        │
 *   │    .centerPanel (flex: 1, scrollable — prompt + refs + generate + preview)│
 *   │  .footerRow (flex-shrink: 0)                                            │
 *   └─────────────────────────────────────────────────────────────────────────┘
 *
 * Each render slot has:
 *   - Editable prompt textarea (auto-generated from event/theme data)
 *   - Reference images strip (provider images auto-loaded + manual upload)
 *   - AI assist to rewrite the prompt
 *   - Provider toggle (Gemini / OpenAI)
 *   - Generate / Regenerate button
 *   - Preview area
 *
 * Uses the presentation wizard's generate-image endpoint under the hood.
 */

import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Skeleton, Button } from '@/components/ui';
import { extractApiError } from '@/lib/axios';
import {
  useCreateDraft,
  useUpdateDraft,
  useGenerateImage,
  usePromptAssist,
  useFinalizeRender,
} from '@/api/useWizardV2';
import { useProvider } from '@/api/useProviderEditor';
import { useThemes } from '@/api/usePresentationWizard';
import { AiAssistInput } from '@/pages/Dashboard/GeneratePresentation/components/AiAssistInput';
import { AddReferenceModal } from '@/pages/Dashboard/GeneratePresentation/phases/AddReferenceModal';
import { ROUTES } from '@/constants';
import type { RenderWizardFormData } from '@/types/wizard';
import type { ReferenceImage } from '@/types/wizardV2';
import styles from './RenderImageStep.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface RenderSlot {
  index: number;
  prompt: string;
  url: string | null;
  status: 'idle' | 'generating' | 'done' | 'error';
}

interface Props {
  formData: RenderWizardFormData;
  onBack: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildRenderPrompt(
  index: number,
  total: number,
  eventName: string,
  themeName: string,
  primaryColor: string,
  accentColor: string,
  userPrompt: string,
): string {
  const angles = [
    'wide establishing shot showing the full venue layout and seating arrangement',
    'close-up of the main stage area with lighting and branding elements',
    'aerial/bird-eye view of the complete event space',
    'detail shot of table settings, centerpieces and decorative elements',
    'entrance and welcome area with signage and guest reception',
    'lounge and networking areas with ambient lighting',
    'food and beverage station with elegant presentation',
    'interactive activity zone or entertainment area',
    'VIP section with premium seating and exclusive amenities',
    'outdoor terrace or garden area with event lighting',
  ];

  const angle = angles[index % angles.length];

  let prompt = `Photorealistic 3D render of an upscale corporate event: ${angle}. `;
  prompt += `Event: "${eventName}". Theme: ${themeName}. `;
  prompt += `Primary color: ${primaryColor}, accent color: ${accentColor}. `;
  prompt += `Studio-quality cinematic lighting, ultra-detailed architecture, professional event styling. `;

  if (userPrompt.trim()) {
    prompt += `Special instructions: ${userPrompt.trim()}. `;
  }

  prompt += `Image ${index + 1} of ${total}. `;
  prompt += `CRITICAL: NO TEXT, no labels, no typography, no words in the image. Photorealistic, professional event venue render.`;

  return prompt;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const RenderImageStep = ({ formData, onBack }: Props) => {
  const navigate = useNavigate();

  // ── Resolve theme data ──────────────────────────────────────────────────

  const { data: themes } = useThemes();
  const selectedTheme = themes?.find((t) => formData.theme_slugs.includes(t.slug));
  const themeName = selectedTheme?.name ?? 'Corporate';
  const primaryColor = selectedTheme?.color_suggestion?.primary ?? '#0C1B33';
  const accentColor = selectedTheme?.color_suggestion?.accent ?? '#C9A84C';

  // ── Fetch venue provider images (if a provider is selected) ─────────────

  const { data: venueProvider } = useProvider(formData.provider_id ?? undefined);

  // ── API mutations ──────────────────────────────────────────────────────

  const createDraft = useCreateDraft();
  const updateDraft = useUpdateDraft();
  const generateImage = useGenerateImage();
  const promptAssist = usePromptAssist();
  const finalizeRender = useFinalizeRender();

  // ── Draft presentation ID ─────────────────────────────────────────────

  const [draftId, setDraftId] = useState<string | null>(null);
  const draftCreatedRef = useRef(false);

  // ── Render slots ──────────────────────────────────────────────────────

  const numRenders = formData.num_renders || 4;
  const [slots, setSlots] = useState<RenderSlot[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // ── Reference images ──────────────────────────────────────────────────

  const [referenceImages, setReferenceImages] = useState<ReferenceImage[]>([]);
  const [slotReferenceIds, setSlotReferenceIds] = useState<Record<number, string[]>>({});
  const [refModalOpen, setRefModalOpen] = useState(false);

  // ── UI state ──────────────────────────────────────────────────────────

  const [defaultProvider, setDefaultProvider] = useState<'gemini' | 'openai'>('gemini');
  const [slotProviders, setSlotProviders] = useState<Record<number, 'gemini' | 'openai'>>({});
  const [assistError, setAssistError] = useState<string | null>(null);

  // ── Initialise slots + create draft on mount ──────────────────────────

  useEffect(() => {
    if (slots.length > 0) return;

    const initialSlots: RenderSlot[] = Array.from({ length: numRenders }, (_, i) => ({
      index: i,
      prompt: buildRenderPrompt(i, numRenders, formData.event_name, themeName, primaryColor, accentColor, formData.prompt),
      url: null,
      status: 'idle' as const,
    }));
    setSlots(initialSlots);
  }, [numRenders, formData.event_name, formData.prompt, themeName, primaryColor, accentColor, slots.length]);

  // Create draft presentation for using generate-image endpoint
  useEffect(() => {
    if (draftCreatedRef.current || draftId) return;
    draftCreatedRef.current = true;

    createDraft.mutate(
      {
        type: 'render',
        city: formData.city,
        theme_slugs: formData.theme_slugs,
        event_name: formData.event_name,
        prompt: formData.prompt,
        logo_url: formData.logo_url,
        provider_id: formData.provider_id,
        num_renders: formData.num_renders,
      } as Record<string, unknown>,
      {
        onSuccess: (data) => {
          setDraftId(data.uuid);
        },
        onError: (err) => {
          toast.error(extractApiError(err, 'Failed to create render draft.'), { duration: 8_000 });
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto-load provider images as references ───────────────────────────

  const providerImagesLoadedRef = useRef(false);

  useEffect(() => {
    if (providerImagesLoadedRef.current || !venueProvider) return;
    providerImagesLoadedRef.current = true;

    const providerImages = (venueProvider.images as Array<{ url: string; name?: string }>) ?? [];
    const refs: ReferenceImage[] = [];

    providerImages.forEach((img, i) => {
      if (img.url) {
        refs.push({
          id: `provider-${i}`,
          url: img.url,
          source: 'uploaded' as const,
        });
      }
    });

    if (formData.logo_url) {
      refs.push({
        id: 'logo',
        url: formData.logo_url,
        source: 'logo' as const,
      });
    }

    if (refs.length > 0) {
      setReferenceImages((prev) => {
        const existingIds = new Set(prev.map((r) => r.id));
        const newRefs = refs.filter((r) => !existingIds.has(r.id));
        return [...prev, ...newRefs];
      });

      // Auto-assign all provider images + logo to all slots
      const allRefIds = refs.map((r) => r.id);
      setSlotReferenceIds((prev) => {
        const updated = { ...prev };
        for (let i = 0; i < numRenders; i++) {
          const existing = new Set(updated[i] ?? []);
          allRefIds.forEach((id) => existing.add(id));
          updated[i] = Array.from(existing);
        }
        return updated;
      });
    }
  }, [venueProvider, formData.logo_url, numRenders]);

  // ── Derived state ─────────────────────────────────────────────────────

  const selectedSlot = slots[selectedIndex] ?? null;
  const activeProvider = slotProviders[selectedIndex] ?? defaultProvider;
  const activeRefIds = slotReferenceIds[selectedIndex] ?? [];
  const activeRefImages = referenceImages.filter((r) => activeRefIds.includes(r.id));

  const doneCount = slots.filter((s) => s.status === 'done' && s.url).length;
  const totalCount = slots.length;

  // ── Generate single render ────────────────────────────────────────────

  const generateSlot = useCallback(
    async (index: number): Promise<void> => {
      if (!draftId) {
        toast.error('Draft not ready yet. Please wait.');
        return;
      }

      const slot = slots[index];
      if (!slot || !slot.prompt.trim()) return;

      const provider = slotProviders[index] ?? defaultProvider;
      const refIds = slotReferenceIds[index] ?? [];
      const refUrls = refIds
        .map((id) => referenceImages.find((r) => r.id === id)?.url)
        .filter((u): u is string => !!u);

      setSlots((prev) =>
        prev.map((s, i) => (i === index ? { ...s, status: 'generating' as const } : s)),
      );

      try {
        const result = await generateImage.mutateAsync({
          id: draftId,
          slotKey: `render-${index}`,
          prompt: slot.prompt,
          provider,
          referenceImageUrls: refUrls,
        });

        setSlots((prev) =>
          prev.map((s, i) =>
            i === index ? { ...s, url: result.url, status: 'done' as const } : s,
          ),
        );

        // Add generated image as a reference for other slots
        setReferenceImages((prev) => {
          const genRefId = `generated-render-${index}`;
          if (prev.some((r) => r.id === genRefId)) {
            return prev.map((r) => (r.id === genRefId ? { ...r, url: result.url } : r));
          }
          return [...prev, { id: genRefId, url: result.url, source: 'generated' as const }];
        });
      } catch (err) {
        setSlots((prev) =>
          prev.map((s, i) => (i === index ? { ...s, status: 'error' as const } : s)),
        );
        toast.error(extractApiError(err, 'Error generating render. Try again.'), {
          duration: 8_000,
        });
      }
    },
    [draftId, slots, slotProviders, defaultProvider, slotReferenceIds, referenceImages, generateImage],
  );

  // ── Generate all idle slots ───────────────────────────────────────────

  const handleGenerateAll = useCallback(() => {
    if (!draftId) {
      toast.error('Draft not ready yet. Please wait.');
      return;
    }

    const idleTasks = slots
      .map((s, i) => (s.status === 'idle' || s.status === 'error' ? i : -1))
      .filter((i) => i >= 0);

    if (idleTasks.length === 0) {
      toast('All renders already generated.', { duration: 3_000 });
      return;
    }

    void Promise.allSettled(idleTasks.map((i) => generateSlot(i))).then((results) => {
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed === 0) {
        toast.success('All renders generated successfully!', { duration: 3_000 });
      } else {
        toast.error(`${failed} render(s) failed. Check each one.`, { duration: 8_000 });
      }
    });
  }, [draftId, slots, generateSlot]);

  // ── Prompt change ─────────────────────────────────────────────────────

  const handlePromptChange = useCallback(
    (value: string) => {
      setSlots((prev) =>
        prev.map((s, i) => (i === selectedIndex ? { ...s, prompt: value } : s)),
      );
    },
    [selectedIndex],
  );

  // ── Provider change ───────────────────────────────────────────────────

  const handleProviderChange = useCallback(
    (provider: 'gemini' | 'openai') => {
      setSlotProviders((prev) => ({ ...prev, [selectedIndex]: provider }));
    },
    [selectedIndex],
  );

  // ── AI Assist ─────────────────────────────────────────────────────────

  const handlePromptAssist = useCallback(
    async (message: string): Promise<void> => {
      if (!draftId) return;

      setAssistError(null);

      try {
        const currentPrompt = slots[selectedIndex]?.prompt ?? '';
        const result = await promptAssist.mutateAsync({
          id: draftId,
          instruction: message,
          currentPrompt,
          slideContext: {
            component: 'render',
            section: 'Render',
            title: formData.event_name,
          },
        });

        setSlots((prev) =>
          prev.map((s, i) => (i === selectedIndex ? { ...s, prompt: result.prompt } : s)),
        );
      } catch {
        const errorMsg = 'Could not improve the prompt. Try again.';
        setAssistError(errorMsg);
        throw new Error(errorMsg);
      }
    },
    [draftId, selectedIndex, slots, promptAssist, formData.event_name],
  );

  // ── Reference image management ────────────────────────────────────────

  const handleReferenceSelect = useCallback(
    (images: ReferenceImage[]) => {
      setReferenceImages((prev) => {
        const existingIds = new Set(prev.map((r) => r.id));
        const newRefs = images.filter((img) => !existingIds.has(img.id));
        return [...prev, ...newRefs];
      });

      setSlotReferenceIds((prev) => {
        const existing = new Set(prev[selectedIndex] ?? []);
        images.forEach((img) => existing.add(img.id));
        return { ...prev, [selectedIndex]: Array.from(existing) };
      });
    },
    [selectedIndex],
  );

  const handleRemoveReference = useCallback(
    (id: string) => {
      setSlotReferenceIds((prev) => ({
        ...prev,
        [selectedIndex]: (prev[selectedIndex] ?? []).filter((rid) => rid !== id),
      }));
    },
    [selectedIndex],
  );

  // ── Finalize ──────────────────────────────────────────────────────────

  const [isFinalizing, setIsFinalizing] = useState(false);

  const handleFinalize = useCallback(async () => {
    if (!draftId) return;

    const generatedUrls = slots.filter((s) => s.url).map((s) => s.url as string);
    if (generatedUrls.length === 0) {
      toast.error('Generate at least one render before finishing.');
      return;
    }

    setIsFinalizing(true);

    try {
      await updateDraft.mutateAsync({
        id: draftId,
        data: {
          generated_images: generatedUrls,
          reference_images: referenceImages.map((r) => r.url).filter(Boolean),
        },
      });

      await finalizeRender.mutateAsync({ id: draftId });

      toast.success('Renders saved successfully!', { duration: 3_000 });
      navigate(ROUTES.dashboardRenders);
    } catch (err) {
      toast.error(extractApiError(err, 'Failed to save renders.'), { duration: 8_000 });
    } finally {
      setIsFinalizing(false);
    }
  }, [draftId, slots, referenceImages, updateDraft, finalizeRender, navigate]);

  // ── Derived generated images for AddReferenceModal ────────────────────

  const generatedImagesForModal = useMemo(
    () =>
      slots
        .filter((s) => s.status === 'done' && s.url)
        .map((s) => ({ url: s.url as string, slideIndex: s.index })),
    [slots],
  );

  // ── Render ────────────────────────────────────────────────────────────

  const isDraftLoading = createDraft.isPending;

  return (
    <div className={styles.container}>
      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className={styles.toolbar} role="toolbar" aria-label="Render generation options">
        <button
          type="button"
          className={styles.backButton}
          onClick={onBack}
          aria-label="Go back to details"
        >
          ← Back
        </button>

        <Button
          variant="secondary"
          size="sm"
          onClick={handleGenerateAll}
          disabled={isDraftLoading || totalCount === 0}
          aria-label="Generate all pending renders"
        >
          Generate all
        </Button>

        <div className={styles.toolbarSpacer} />

        <fieldset className={styles.providerFieldset}>
          <legend className={styles.providerLegend}>Default provider</legend>
          <label className={styles.providerLabel}>
            <input
              type="radio"
              name="default-provider"
              value="gemini"
              checked={defaultProvider === 'gemini'}
              onChange={() => setDefaultProvider('gemini')}
              className={styles.providerRadio}
            />
            Gemini
          </label>
          <label className={styles.providerLabel}>
            <input
              type="radio"
              name="default-provider"
              value="openai"
              checked={defaultProvider === 'openai'}
              onChange={() => setDefaultProvider('openai')}
              className={styles.providerRadio}
            />
            OpenAI
          </label>
        </fieldset>

        <span className={styles.slotCounter} aria-live="polite" aria-atomic>
          {isDraftLoading ? 'Creating draft...' : `${doneCount}/${totalCount} renders`}
        </span>
      </div>

      {/* ── Editor area ──────────────────────────────────────────────────── */}
      <div className={styles.editorArea}>
        {/* ── Left sidebar ─────────────────────────────────────────────── */}
        <div className={styles.sidebar} aria-label="Render list">
          <div className={styles.sidebarHeader}>
            <h2 className={styles.sidebarHeaderTitle}>Renders</h2>
          </div>

          {isDraftLoading ? (
            <div className={styles.sidebarSkeletonList} aria-label="Loading..." aria-busy>
              {Array.from({ length: numRenders }, (_, i) => (
                <Skeleton key={i} variant="rect" height={80} />
              ))}
            </div>
          ) : (
            <div className={styles.slideList} role="list">
              {slots.map((slot, i) => {
                const isActive = selectedIndex === i;
                return (
                  <div
                    key={i}
                    role="listitem"
                    className={[
                      styles.renderCard,
                      isActive ? styles.renderCardSelected : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => setSelectedIndex(i)}
                    aria-label={`Render ${i + 1}`}
                    aria-selected={isActive}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedIndex(i);
                      }
                    }}
                  >
                    {slot.url ? (
                      <img
                        src={slot.url}
                        alt={`Render ${i + 1}`}
                        className={styles.renderThumb}
                      />
                    ) : (
                      <div className={styles.renderThumbPlaceholder}>
                        <span className={styles.renderThumbNumber}>{i + 1}</span>
                      </div>
                    )}

                    <span
                      className={[
                        styles.statusBadge,
                        styles[`statusBadge--${slot.status}`],
                      ].join(' ')}
                      aria-label={`Status: ${slot.status}`}
                    >
                      {slot.status === 'generating' && (
                        <span className={styles.statusDot} aria-hidden />
                      )}
                      {slot.status === 'done' && <span aria-hidden>&#10003;</span>}
                      {slot.status === 'error' && <span aria-hidden>!</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Center panel ──────────────────────────────────────────────── */}
        <div className={styles.centerPanel} aria-label="Render generation panel">
          {selectedSlot === null ? (
            <div className={styles.emptyCenter}>
              <p>Select a render from the left.</p>
            </div>
          ) : (
            <div className={styles.panelBody}>
              {/* ── Header ──────────────────────────────────────────── */}
              <div className={styles.panelHeader}>
                <p className={styles.panelSlideTitle}>
                  Render {selectedIndex + 1} of {totalCount}
                </p>
                <p className={styles.panelSlideInfo}>
                  Theme: {themeName} · {formData.event_name}
                </p>
              </div>

              {/* ── Nav arrows + controls ──────────────────────────── */}
              <div className={styles.navControlsRow}>
                <button
                  type="button"
                  className={styles.navArrowTall}
                  disabled={selectedIndex <= 0}
                  onClick={() => setSelectedIndex((p) => Math.max(0, p - 1))}
                  aria-label="Previous render"
                >
                  ‹
                </button>

                <div className={styles.controlsCol}>
                  {/* ── Prompt textarea ──────────────────────────── */}
                  <div className={styles.promptSection}>
                    <label className={styles.sectionLabel} htmlFor="render-prompt">
                      Image prompt
                    </label>
                    <textarea
                      id="render-prompt"
                      className={styles.promptTextarea}
                      value={selectedSlot.prompt}
                      onChange={(e) => handlePromptChange(e.target.value)}
                      rows={5}
                      disabled={selectedSlot.status === 'generating'}
                      aria-label="Edit render prompt"
                    />
                  </div>

                  {/* ── Reference images strip ──────────────────── */}
                  <div className={styles.refsSection}>
                    <span className={styles.sectionLabel}>References</span>
                    <div className={styles.refsRow}>
                      {activeRefImages.map((ref) => (
                        <div key={ref.id} className={styles.refChip}>
                          <img
                            src={ref.url}
                            alt={ref.source}
                            className={styles.refChipImg}
                          />
                          <button
                            type="button"
                            className={styles.refChipRemove}
                            onClick={() => handleRemoveReference(ref.id)}
                            aria-label={`Remove reference ${ref.source}`}
                          >
                            &#10005;
                          </button>
                        </div>
                      ))}

                      <button
                        type="button"
                        className={styles.addRefButton}
                        onClick={() => setRefModalOpen(true)}
                        aria-label="Add reference image"
                      >
                        + Add reference
                      </button>
                    </div>
                  </div>

                  {/* ── AI Assist ────────────────────────────────── */}
                  <div className={styles.assistSection}>
                    <span className={styles.sectionLabel}>Adjust prompt</span>
                    <AiAssistInput
                      placeholder="Make it more minimalist, add people, change the lighting..."
                      onSubmit={handlePromptAssist}
                      isLoading={promptAssist.isPending}
                      error={assistError}
                      onClearError={() => setAssistError(null)}
                    />
                  </div>

                  {/* ── Provider toggle ──────────────────────────── */}
                  <div className={styles.optionsRow}>
                    <fieldset className={styles.providerFieldset}>
                      <legend className={styles.providerLegend}>Provider</legend>
                      <label className={styles.providerLabel}>
                        <input
                          type="radio"
                          name={`slot-provider-${selectedIndex}`}
                          value="gemini"
                          checked={activeProvider === 'gemini'}
                          onChange={() => handleProviderChange('gemini')}
                          className={styles.providerRadio}
                        />
                        Gemini
                      </label>
                      <label className={styles.providerLabel}>
                        <input
                          type="radio"
                          name={`slot-provider-${selectedIndex}`}
                          value="openai"
                          checked={activeProvider === 'openai'}
                          onChange={() => handleProviderChange('openai')}
                          className={styles.providerRadio}
                        />
                        OpenAI
                      </label>
                    </fieldset>
                  </div>

                  {/* ── Generate / Regenerate ────────────────────── */}
                  <div className={styles.generateRow}>
                    <Button
                      variant="primary"
                      size="md"
                      onClick={() => void generateSlot(selectedIndex)}
                      disabled={
                        !draftId ||
                        !selectedSlot.prompt.trim() ||
                        selectedSlot.status === 'generating'
                      }
                      aria-label={selectedSlot.url ? 'Regenerate render' : 'Generate render'}
                    >
                      {selectedSlot.status === 'generating'
                        ? 'Generating...'
                        : selectedSlot.url
                          ? 'Regenerate'
                          : 'Generate'}
                    </Button>
                  </div>
                </div>

                <button
                  type="button"
                  className={styles.navArrowTall}
                  disabled={selectedIndex >= totalCount - 1}
                  onClick={() => setSelectedIndex((p) => Math.min(totalCount - 1, p + 1))}
                  aria-label="Next render"
                >
                  ›
                </button>
              </div>

              {/* ── Preview area ──────────────────────────────────── */}
              <div className={styles.previewSection}>
                <span className={styles.sectionLabel}>Preview</span>
                <div className={styles.previewBox} aria-label="Render preview">
                  {selectedSlot.status === 'generating' ? (
                    <Skeleton variant="rect" height={280} />
                  ) : selectedSlot.url ? (
                    <img
                      src={selectedSlot.url}
                      alt={`Render ${selectedIndex + 1}`}
                      className={styles.previewImg}
                    />
                  ) : (
                    <div className={styles.previewEmpty}>
                      <p>Generate the render to see a preview.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <div className={styles.footerRow}>
        <Button
          variant="primary"
          size="md"
          onClick={handleFinalize}
          disabled={doneCount === 0 || isFinalizing}
          aria-label="Finish and save renders"
        >
          {isFinalizing ? 'Saving...' : `Finish (${doneCount}/${totalCount} renders) →`}
        </Button>
      </div>

      {/* ── AddReferenceModal ─────────────────────────────────────────────── */}
      <AddReferenceModal
        isOpen={refModalOpen}
        onClose={() => setRefModalOpen(false)}
        onSelect={handleReferenceSelect}
        logoUrl={formData.logo_url}
        generatedImages={generatedImagesForModal}
      />
    </div>
  );
};
